/**
 * Event Team Command-Log Clearance — browser-side Elasticsearch runner (guide §3).
 *
 * The backend owns team/check definitions and generates ready-to-run ES query specs
 * (`GET /events/checks/{id}/es-query`), but it CANNOT reach the command-log ES cluster
 * (it sits behind Cloudflare Access). The **browser** executes the queries with the
 * operator's Access session and POSTs the results back.
 *
 * Key responsibilities here:
 * 1. **Field remapping** — the backend's `field_hints` assume generic field names
 *    (`player_uuid`, `command`, `@timestamp`, `server_fqdn`, `cluster_id`). This
 *    deployment's command-log index (`cmd_logs_v1`) actually uses `fromPlayerUuid`,
 *    `command`, `ts_ingested`, `FQDN`, `clusterId`. We string-replace field names
 *    in the query (guide: "the spec is deliberately plain JSON so this is trivial")
 *    and remap the hit extraction. The mapping is editable in the UI + persisted.
 * 2. **`search_after` pagination** — the generated query pages at `size: 100`,
 *    sorted by timestamp ascending; teams that exceed 100 hits are followed up and
 *    merged (with a safety cap).
 * 3. **Error taxonomy** — 403 = the operator's Access session lacks ES access.
 *    Any team failure must block submission entirely (no partial results).
 */

import { elasticsearchService } from './elasticsearchService';
import type {
  EsQueryFieldHints,
  EsQueryTeamSpec,
  SubmitCheckViolation,
} from '../types/eventTeam';

// ── Deployment configuration ────────────────────────────────────────────────

export interface ClearanceFieldMapping {
  /** hint field name → actual index field name. */
  player_uuid: string;
  command: string;
  timestamp: string;
  server: string;
  cluster: string;
}

export interface ClearanceRunnerConfig {
  /** Command-log index (or index pattern) to query. */
  index: string;
  mapping: ClearanceFieldMapping;
  /**
   * Rewrite `<field>.keyword` references to the bare mapped field. The backend's
   * generated queries suffix `.keyword` onto its command/server/cluster matchers,
   * but `cmd_logs_v1` is mapped with `dynamic: "false"` and every string field is
   * a ROOT-LEVEL `keyword` — no `.keyword` multi-fields exist, so a filter on one
   * matches zero documents and would silently report every team as "clear"
   * (verified against the live index mapping). Disable only for deployments whose
   * index genuinely uses text + `.keyword` multi-fields.
   */
  stripKeywordSuffixes: boolean;
  /**
   * Expand command matchers to match values both WITH and WITHOUT a leading
   * slash. `cmd_logs_v1` stores commands slash-less (`"home"`, `"enchant …"` —
   * verified from live documents), but humans define checks as `/home`, `/fly`…
   * Without this, every prefix/exact matcher misses and teams look falsely "clear".
   */
  normalizeCommandSlashes: boolean;
  /** Per-team hit safety cap for `search_after` pagination. */
  maxHitsPerTeam: number;
}

/**
 * Defaults tuned to THIS deployment's command-log index (`cmd_logs_v1`).
 * Confirmed mapping: `dynamic:"false"`; keyword: FQDN, args, clusterId, command,
 * fromPlayerName, fromPlayerUuid, serverId, type, version; date: ts_ingested;
 * playerPos{dimension keyword, x/y/z float}; raw disabled.
 */
export const DEFAULT_CLEARANCE_CONFIG: ClearanceRunnerConfig = {
  index: 'cmd_logs_v1',
  mapping: {
    player_uuid: 'fromPlayerUuid',
    command: 'command',
    timestamp: 'ts_ingested',
    server: 'FQDN',
    cluster: 'clusterId',
  },
  stripKeywordSuffixes: true,
  normalizeCommandSlashes: true,
  maxHitsPerTeam: 5000,
};

const STORAGE_KEY = 'event-clearance-runner-config-v1';

export function loadClearanceConfig(): ClearanceRunnerConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CLEARANCE_CONFIG, mapping: { ...DEFAULT_CLEARANCE_CONFIG.mapping } };
    const parsed = JSON.parse(raw) as Partial<ClearanceRunnerConfig>;
    return {
      index: typeof parsed.index === 'string' && parsed.index ? parsed.index : DEFAULT_CLEARANCE_CONFIG.index,
      mapping: { ...DEFAULT_CLEARANCE_CONFIG.mapping, ...(parsed.mapping ?? {}) },
      stripKeywordSuffixes:
        typeof parsed.stripKeywordSuffixes === 'boolean'
          ? parsed.stripKeywordSuffixes
          : DEFAULT_CLEARANCE_CONFIG.stripKeywordSuffixes,
      normalizeCommandSlashes:
        typeof parsed.normalizeCommandSlashes === 'boolean'
          ? parsed.normalizeCommandSlashes
          : DEFAULT_CLEARANCE_CONFIG.normalizeCommandSlashes,
      maxHitsPerTeam:
        typeof parsed.maxHitsPerTeam === 'number' && parsed.maxHitsPerTeam > 0
          ? parsed.maxHitsPerTeam
          : DEFAULT_CLEARANCE_CONFIG.maxHitsPerTeam,
    };
  } catch {
    return { ...DEFAULT_CLEARANCE_CONFIG, mapping: { ...DEFAULT_CLEARANCE_CONFIG.mapping } };
  }
}

export function saveClearanceConfig(config: ClearanceRunnerConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* localStorage unavailable — non-fatal */
  }
}

// ── Field remapping ─────────────────────────────────────────────────────────

/**
 * Build the hint→actual replacement table from the spec's `field_hints` and the
 * operator's mapping. Matching respects field-name boundaries: a hint matches
 * either exactly or as a prefix followed by `.`.
 *
 * When `stripKeywordSuffixes` is on, explicit `X.keyword → actual` pairs are
 * emitted for BOTH the hint and the actual field name (covering identity
 * mappings like `command`, which have no plain rename pair) so the bogus
 * suffixes the backend generates are removed rather than carried over.
 */
function buildReplacements(
  hints: EsQueryFieldHints,
  mapping: ClearanceFieldMapping,
  stripKeywordSuffixes: boolean,
): Array<[string, string]> {
  const base: Array<[string, string]> = [
    [hints.player_uuid_field, mapping.player_uuid],
    [hints.command_field, mapping.command],
    [hints.timestamp_field, mapping.timestamp],
    [hints.server_field, mapping.server],
    [hints.cluster_field, mapping.cluster],
  ];

  const pairs = new Map<string, string>();
  for (const [from, to] of base) {
    if (!from || !to) continue;
    if (stripKeywordSuffixes) {
      // `hint.keyword` → actual, and `actual.keyword` → actual (no multi-fields exist).
      pairs.set(`${from}.keyword`, to);
      pairs.set(`${to}.keyword`, to);
    }
    if (from !== to) pairs.set(from, to);
  }
  // Longest key first so `cluster_id.keyword` wins over `cluster_id`,
  // and `server_fqdn` over a hypothetical `server`.
  return Array.from(pairs.entries()).sort((a, b) => b[0].length - a[0].length);
}

function remapFieldName(name: string, replacements: Array<[string, string]>): string {
  for (const [from, to] of replacements) {
    if (name === from) return to;
    if (name.startsWith(`${from}.`)) return `${to}${name.slice(from.length)}`;
  }
  return name;
}

/**
 * Deep-walk the ES query JSON replacing every object key and string value that
 * names a hint field (exact or `hint.suffix`) with the mapped field. Handles the
 * `sort` clause, `terms`/`term`/`range`/`prefix`/`regexp` filters and any other
 * shape the backend emits, since replacement is purely name-based.
 */
export function remapQuery(
  query: unknown,
  hints: EsQueryFieldHints,
  mapping: ClearanceFieldMapping,
  stripKeywordSuffixes = true,
): unknown {
  const replacements = buildReplacements(hints, mapping, stripKeywordSuffixes);
  if (replacements.length === 0) return query;

  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(walk);
    if (node !== null && typeof node === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        out[remapFieldName(key, replacements)] = walk(value);
      }
      return out;
    }
    if (typeof node === 'string') return remapFieldName(node, replacements);
    return node;
  };
  return walk(query);
}

// ── Command slash normalization ─────────────────────────────────────────────

function commandValueVariants(value: string): string[] {
  const bare = value.startsWith('/') ? value.slice(1) : value;
  if (!bare) return [value];
  const slashed = `/${bare}`;
  return [bare, slashed];
}

/**
 * Expand a single `prefix`/`term` clause on the command field into one clause per
 * slash variant (`/fly` → `fly` + `/fly`). Returns `null` when the clause is not
 * a command matcher (left untouched).
 */
function expandClause(item: unknown, commandField: string): unknown[] | null {
  if (!item || typeof item !== 'object' || Array.isArray(item)) return null;
  const obj = item as Record<string, unknown>;
  const keys = Object.keys(obj);
  if (keys.length !== 1) return null;
  const clauseType = keys[0];
  if (clauseType !== 'prefix' && clauseType !== 'term') return null;
  const body = obj[clauseType];
  if (!body || typeof body !== 'object' || Array.isArray(body)) return null;
  const bodyObj = body as Record<string, unknown>;
  if (!(commandField in bodyObj)) return null;

  const fieldVal = bodyObj[commandField];
  if (typeof fieldVal === 'string') {
    const variants = commandValueVariants(fieldVal);
    if (variants.length === 1) return null;
    return variants.map((v) => ({ [clauseType]: { [commandField]: v } }));
  }
  if (fieldVal && typeof fieldVal === 'object' && typeof (fieldVal as Record<string, unknown>).value === 'string') {
    const params = fieldVal as Record<string, unknown>;
    const variants = commandValueVariants(params.value as string);
    if (variants.length === 1) return null;
    return variants.map((v) => ({ [clauseType]: { [commandField]: { ...params, value: v } } }));
  }
  return null;
}

/**
 * Walk the (already remapped) query and make every command matcher tolerant of
 * the leading slash: `prefix`/`term` clauses inside arrays are duplicated per
 * variant (safe under `should` + `minimum_should_match: 1`), and `terms` value
 * lists gain both forms. `regexp` matchers are left to the operator.
 */
export function expandCommandSlashVariants(query: unknown, commandField: string): unknown {
  const walk = (node: unknown): unknown => {
    if (Array.isArray(node)) {
      const out: unknown[] = [];
      for (const raw of node) {
        const item = walk(raw);
        const expanded = expandClause(item, commandField);
        if (expanded) out.push(...expanded);
        else out.push(item);
      }
      return out;
    }
    if (node !== null && typeof node === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
        if (
          key === 'terms' &&
          value &&
          typeof value === 'object' &&
          !Array.isArray(value) &&
          Array.isArray((value as Record<string, unknown>)[commandField])
        ) {
          const list = (value as Record<string, unknown>)[commandField] as unknown[];
          const expandedValues = Array.from(
            new Set(list.flatMap((v) => (typeof v === 'string' ? commandValueVariants(v) : [v]))),
          );
          out[key] = { ...(value as Record<string, unknown>), [commandField]: expandedValues };
          continue;
        }
        out[key] = walk(value);
      }
      return out;
    }
    return node;
  };
  return walk(query);
}

// ── Execution ───────────────────────────────────────────────────────────────

export interface ClearanceEsHit {
  _index: string;
  _id: string;
  _source: Record<string, unknown>;
  sort?: unknown[];
}

interface ClearanceEsResponse {
  timed_out?: boolean;
  _shards?: { failed?: number };
  hits?: { total?: { value?: number; relation?: string } | number; hits?: ClearanceEsHit[] };
}

export class ClearanceEsError extends Error {
  /** True when the failure looks like a Cloudflare Access / permission problem. */
  accessDenied: boolean;

  constructor(message: string, accessDenied: boolean) {
    super(message);
    this.name = 'ClearanceEsError';
    this.accessDenied = accessDenied;
  }
}

export interface TeamRunResult {
  check_signature?: string;
  /** Exact query roster, only present when the standard query was fully evaluated. */
  checked_uuids?: string[];
  team_id: string;
  team_name: string;
  status: 'clear' | 'flagged';
  members_checked: number;
  violations: SubmitCheckViolation[];
  /** Raw hit count (may exceed violations if capped). */
  hits: number;
  capped: boolean;
}

function extractTimestamp(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    return Number.isFinite(ms) ? ms : null;
  }
  return null;
}

function asString(value: unknown): string | null {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return null;
}

/**
 * Execute one team's query (with remapping + `search_after` pagination) and
 * convert hits into violation rows (`es_doc_ref` keeps the audit trail back to
 * the log document — guide §3.3).
 */
export async function runTeamQuery(
  team: EsQueryTeamSpec,
  hints: EsQueryFieldHints,
  config: ClearanceRunnerConfig,
  /** Operator-edited final query (advanced escape hatch); defaults to the remapped spec query. */
  overrideQuery?: Record<string, unknown>,
): Promise<TeamRunResult> {
  let baseQuery =
    overrideQuery ??
    (remapQuery(team.query, hints, config.mapping, config.stripKeywordSuffixes) as Record<string, unknown>);
  // Operator-edited overrides are used verbatim; only spec-derived queries are normalized.
  if (!overrideQuery && config.normalizeCommandSlashes) {
    baseQuery = expandCommandSlashVariants(baseQuery, config.mapping.command) as Record<string, unknown>;
  }
  const pageSize = typeof baseQuery.size === 'number' ? baseQuery.size : 100;

  const mappedFields = {
    player: config.mapping.player_uuid,
    command: config.mapping.command,
    timestamp: config.mapping.timestamp,
    server: config.mapping.server,
  };

  const allHits: ClearanceEsHit[] = [];
  let searchAfter: unknown[] | undefined;
  let capped = false;
  let total: number | null = null;

  for (;;) {
    const body: Record<string, unknown> = { ...baseQuery, track_total_hits: true };
    if (searchAfter) body.search_after = searchAfter;

    let res: ClearanceEsResponse;
    try {
      res = await elasticsearchService.searchIndex<ClearanceEsResponse>(config.index, body);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      const accessDenied = /\b(401|403)\b/.test(message);
      throw new ClearanceEsError(
        accessDenied
          ? `Elasticsearch refused the query for "${team.team_name}" — your Cloudflare Access session lacks ES access (${message}).`
          : `Elasticsearch query failed for "${team.team_name}": ${message}`,
        accessDenied,
      );
    }

    const rawTotal = res.hits?.total;
    const exactTotal = typeof rawTotal === 'number' ? rawTotal : rawTotal?.relation === 'eq' ? rawTotal.value : undefined;
    if (res.timed_out !== false || res._shards?.failed !== 0 || !Array.isArray(res.hits?.hits) || !Number.isSafeInteger(exactTotal) || exactTotal! < 0) {
      throw new ClearanceEsError('The log search returned incomplete results. Run the check again; these results cannot prove a player passed.', false);
    }
    if (total !== null && total !== exactTotal) throw new ClearanceEsError('The log results changed during the search. Run the check again.', false);
    total = exactTotal!;
    const hits = res.hits!.hits!;
    allHits.push(...hits);

    if (hits.length < pageSize) break; // last page
    if (allHits.length >= config.maxHitsPerTeam) {
      capped = true;
      break;
    }
    const last = hits[hits.length - 1];
    if (!last?.sort || last.sort.length === 0 || JSON.stringify(last.sort) === JSON.stringify(searchAfter)) { capped = true; break; }
    searchAfter = last.sort;
  }

  if (!capped && allHits.length !== total) throw new ClearanceEsError('Some matching log entries were missing. Run the check again.', false);

  const violations: SubmitCheckViolation[] = allHits.slice(0, config.maxHitsPerTeam).map((h) => ({
    user_uuid: asString(h._source[mappedFields.player]) ?? 'unknown',
    command: asString(h._source[mappedFields.command]) ?? '',
    used_at: extractTimestamp(h._source[mappedFields.timestamp]),
    server: asString(h._source[mappedFields.server]),
    es_doc_ref: `${h._index}/${h._id}`,
  }));

  return {
    checked_uuids: !capped && !overrideQuery ? [...team.member_uuids] : undefined,
    team_id: team.team_id,
    team_name: team.team_name,
    status: violations.length > 0 ? 'flagged' : 'clear',
    members_checked: team.member_uuids.length,
    violations,
    hits: allHits.length,
    capped,
  };
}
